import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { GatedButton } from '@/components/GatedButton';
import { Icon } from '@/components/Icon';
import { AppsTable } from '@/features/apps/AppsTable';
import { useAppActions } from '@/features/apps/useAppActions';
import { RepoChecklist } from './RepoChecklist';
import { StepCard, StepNote } from './StepCard';
import { RowsSkeleton, type StepProps } from './StepShared';
import { appsEmptyDescription, appsNote, appsSummary, appsTitle, listView } from './stepNotesModel';
import { stepNumber } from './steps';
import type { Setup, SetupSearch } from './useSetup';

type AppActions = ReturnType<typeof useAppActions>;

interface BodyProps {
  setup: Setup;
  actions: AppActions;
  onSelect: (next: SetupSearch) => void;
}

/** The apps table and, under it, what the app folder needs before its first build. */
function AppsList({ setup, actions, onSelect, projectSlug }: BodyProps & { projectSlug: string }) {
  const { app } = setup;
  return (
    <>
      <AppsTable apps={setup.apps.data!} projectSlug={projectSlug} bundleIds={setup.bundleIds} selectedId={app?.id} onSelect={(a) => onSelect({ project: projectSlug, app: a.slug })} actionsFor={actions.actionsFor} />
      <RepoChecklist app={app} />
    </>
  );
}

function AppsBody({ setup, actions, onSelect }: BodyProps) {
  const { project, apps } = setup;
  if (!project) return <StepNote tone="muted">Create the project first (step {stepNumber('project')}).</StepNote>;
  const view = listView(apps, apps.data);
  if (view === 'loading') return <RowsSkeleton />;
  if (view === 'error') return <ErrorState compact error={apps.error} onRetry={() => void apps.refetch()} title="Couldn't load the apps" />;
  if (view === 'list') return <AppsList setup={setup} actions={actions} onSelect={onSelect} projectSlug={project.slug} />;
  return <EmptyState compact testId="gs-apps-empty" icon="apps" title={`No app in ${project.name} yet`} description={appsEmptyDescription(setup.bitrise)} />;
}

/** Step 3 · Apps of the selected project: connect (Expo until P4), pick, edit, archive; plus the app-folder checklist. */
export function AppsStep({ setup, index, onSelect }: StepProps) {
  const project = setup.project;
  const actions = useAppActions({
    project,
    onConnected: (app) => onSelect({ project: project?.slug, app: app.slug }),
    onArchived: (app) => (app.id === setup.app?.id ? onSelect({ project: project?.slug }) : undefined),
  });
  const current = setup.progress.next === 'app';
  return (
    <>
      <StepCard
        state={setup.state('app')}
        current={current}
        index={index}
        title={appsTitle(project?.name)}
        summary={appsSummary(setup.bitrise)}
        collapsedNote={appsNote(setup.apps.data, project?.name)}
        footer={
          <GatedButton size="sm" variant={current ? 'default' : 'outline'} data-testid="gs-connect-app" can={actions.can} blockedReason={project ? undefined : 'Create the project first'} onClick={actions.connect}>
            <Icon name="add" size={16} />
            Connect app
          </GatedButton>
        }
      >
        <AppsBody setup={setup} actions={actions} onSelect={onSelect} />
      </StepCard>
      {actions.overlays}
    </>
  );
}
